import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChartLine, Cpu, Lightning, Shield, Robot, CurrencyDollar, Users } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import AdminNav from './AdminNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [m2mStats, setM2mStats] = useState(null);
  const [paymentsRace, setPaymentsRace] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [a, m, pr] = await Promise.all([
        axios.get(`${API_URL}/api/analytics`,         { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/admin/m2m-stats`,   { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/agent/payments-race`),
      ]);
      setAnalytics(a.data);
      setM2mStats(m.data);
      setPaymentsRace(pr.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-['JetBrains_Mono'] text-[#94A3B8]">Loading...</p>
      </div>
    );
  }

  const chartData = [
    {
      name: 'Daily',
      'Page Hits': analytics?.page_hits?.daily || 0,
      'RFQ Submissions': analytics?.rfq_submissions?.daily || 0
    },
    {
      name: 'Weekly',
      'Page Hits': analytics?.page_hits?.weekly || 0,
      'RFQ Submissions': analytics?.rfq_submissions?.weekly || 0
    },
    {
      name: 'Monthly',
      'Page Hits': analytics?.page_hits?.monthly || 0,
      'RFQ Submissions': analytics?.rfq_submissions?.monthly || 0
    }
  ];

  return (
    <div className="min-h-screen bg-[#05050A]">
      <AdminNav title="Admin Dashboard" titleTestId="admin-dashboard-title" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Page Hits Cards */}
          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="daily-hits-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Daily Web Hits
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.page_hits?.daily || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="weekly-hits-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Weekly Web Hits
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.page_hits?.weekly || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="monthly-hits-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Monthly Web Hits
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.page_hits?.monthly || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="total-hits-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Total Web Hits
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#10B981]">
              {analytics?.page_hits?.total || 0}
            </p>
          </div>
        </div>

        {/* RFQ Submission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="daily-rfq-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Daily RFQ Submissions
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.rfq_submissions?.daily || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="weekly-rfq-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Weekly RFQ Submissions
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.rfq_submissions?.weekly || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="monthly-rfq-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Monthly RFQ Submissions
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF]">
              {analytics?.rfq_submissions?.monthly || 0}
            </p>
          </div>

          <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-6" data-testid="total-rfq-card">
            <h3 className="text-sm font-['JetBrains_Mono'] uppercase tracking-wider text-[#94A3B8] mb-2">
              Total RFQ Submissions
            </h3>
            <p className="text-4xl font-['JetBrains_Mono'] font-bold text-[#10B981]">
              {analytics?.rfq_submissions?.total || 0}
            </p>
          </div>
        </div>

        {/* M2M / Autonomous Agent Traffic */}
        <div className="mb-12" data-testid="m2m-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-['JetBrains_Mono'] font-bold flex items-center gap-2">
              <Cpu size={26} weight="fill" className="text-[#A855F7]" />
              <span>M2M / Autonomous Agent Traffic</span>
            </h2>
            <span className="bg-[#A855F7]/15 text-[#A855F7] border border-[#A855F7]/30 px-3 py-1 rounded-full text-xs font-['JetBrains_Mono'] whitespace-nowrap">
              <Robot size={12} weight="fill" className="inline-block mr-1 -mt-0.5" /> AI Agents
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <M2MTile testid="m2m-total" label="M2M Total (All-Time)"
                     icon={<Cpu size={14} weight="fill" />}
                     value={m2mStats?.m2m_total?.total}
                     hint={`${m2mStats?.m2m_total?.unique_agents || 0} unique agents`} />
            <M2MTile testid="m2m-today" label="M2M Today"
                     icon={<Lightning size={14} weight="fill" />}
                     value={m2mStats?.m2m_today?.total}
                     hint={`${m2mStats?.m2m_today?.unique_agents || 0} unique agents`} />
            <M2MTile testid="m2m-week" label="M2M This Week"
                     icon={<Lightning size={14} weight="fill" />}
                     value={m2mStats?.m2m_week?.total}
                     hint={`${m2mStats?.m2m_week?.unique_agents || 0} unique agents`} />
            <M2MTile testid="m2m-month" label="M2M This Month"
                     icon={<Lightning size={14} weight="fill" />}
                     value={m2mStats?.m2m_month?.total}
                     hint={`${m2mStats?.m2m_month?.unique_agents || 0} unique agents`} />
            <M2MTile testid="apikey-total" label="API Key Requests (Total)"
                     icon={<Shield size={14} weight="fill" />}
                     value={m2mStats?.api_key_total?.total}
                     hint="Authenticated agent requests" />
            <M2MTile testid="apikey-today" label="API Key Requests (Today)"
                     icon={<Shield size={14} weight="fill" />}
                     value={m2mStats?.api_key_today?.total}
                     hint="Authenticated today" />
          </div>
          <p className="text-[10px] text-[#64748B] mt-3 font-['JetBrains_Mono']">
            Tracks every hit to <code>/api/agent/*</code>, <code>/api/agent/x402/*</code>, <code>/api/agent/mcp/*</code>, and well-known agent files. Unique agents are computed from API key prefix when authenticated, else from source IP.
          </p>
        </div>

        {/* On-Chain Commerce Volume (moved from public Payments Race) */}
        <div className="mb-12" data-testid="onchain-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl md:text-2xl font-['JetBrains_Mono'] font-bold flex items-center gap-2">
              <CurrencyDollar size={26} weight="fill" className="text-[#10B981]" />
              <span>On-Chain Commerce</span>
            </h2>
            <span className="bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 px-3 py-1 rounded-full text-xs font-['JetBrains_Mono'] whitespace-nowrap">
              USDC · Base
            </span>
          </div>

          {/* Big hero volume strip */}
          <div className="bg-gradient-to-br from-[#0F2027] via-[#0B1120] to-[#1A0B2E] border border-[#10B981]/25 rounded-xl p-5 mb-4">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] text-[#10B981] uppercase tracking-widest font-['JetBrains_Mono']">All-Time Payment Volume</p>
                <p className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-white mt-1" data-testid="admin-alltime-vol">
                  ${Number(paymentsRace?.volume_all?.volume_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#A855F7] uppercase tracking-widest font-['JetBrains_Mono']">Total Transactions</p>
                <p className="text-xl sm:text-2xl font-['JetBrains_Mono'] font-bold text-white">
                  {Number(paymentsRace?.volume_all?.transactions || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <FinanceTile testid="vol-24h-card"    color="#10B981" Icon={CurrencyDollar} label="Volume 24h"
                         value={`$${Number(paymentsRace?.volume_24h?.volume_usd || 0).toFixed(2)}`}
                         hint={`${Number(paymentsRace?.volume_24h?.transactions || 0)} tx`} />
            <FinanceTile testid="vol-30d-card"    color="#007AFF" Icon={ChartLine} label="Volume 30d"
                         value={`$${Number(paymentsRace?.volume_30d?.volume_usd || 0).toFixed(2)}`}
                         hint={`${Number(paymentsRace?.volume_30d?.transactions || 0)} tx`} />
            <FinanceTile testid="m2m-30d-card"    color="#A855F7" Icon={Cpu} label="M2M Requests 30d"
                         value={Number(paymentsRace?.m2m_requests?.d30 || 0).toLocaleString()}
                         hint={`${Number(paymentsRace?.m2m_requests?.unique_agents_24h || 0)} agents 24h`} />
            <FinanceTile testid="buyers-30d-card" color="#F59E0B" Icon={Users} label="Unique Buyers 30d"
                         value={Number(paymentsRace?.volume_30d?.unique_buyers || 0).toLocaleString()}
                         hint="Distinct wallets" />
          </div>
        </div>

        {/* Chart */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-8 mb-12">
          <h2 className="text-2xl font-['JetBrains_Mono'] font-bold mb-6 flex items-center gap-2">
            <ChartLine size={28} className="text-[#007AFF]" />
            Analytics Overview
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="name" stroke="#94A3B8" style={{ fontFamily: 'JetBrains Mono' }} />
              <YAxis stroke="#94A3B8" style={{ fontFamily: 'JetBrains Mono' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#11141D', 
                  border: '1px solid #1E293B',
                  borderRadius: '8px',
                  fontFamily: 'JetBrains Mono'
                }} 
              />
              <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono' }} />
              <Bar dataKey="Page Hits" fill="#007AFF" />
              <Bar dataKey="RFQ Submissions" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* RFQ Status Summary - Clickable to filter */}
        <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-['JetBrains_Mono'] font-bold mb-6">
            RFQ Status Summary
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div 
              className="text-center p-4 rounded-lg bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 cursor-pointer transition-colors"
              onClick={() => navigate('/admin/rfqs?status=Pending')}
              data-testid="pending-rfq-card"
            >
              <p className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-[#F59E0B] mb-2">
                {analytics?.rfq_by_status?.pending || 0}
              </p>
              <p className="text-[#94A3B8] font-['JetBrains_Mono'] text-sm sm:text-base">Pending</p>
            </div>
            <div 
              className="text-center p-4 rounded-lg bg-[#007AFF]/10 hover:bg-[#007AFF]/20 cursor-pointer transition-colors"
              onClick={() => navigate('/admin/rfqs?status=Quoted')}
              data-testid="quoted-rfq-card"
            >
              <p className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-[#007AFF] mb-2">
                {analytics?.rfq_by_status?.quoted || 0}
              </p>
              <p className="text-[#94A3B8] font-['JetBrains_Mono'] text-sm sm:text-base">Quoted</p>
            </div>
            <div 
              className="text-center p-4 rounded-lg bg-[#10B981]/10 hover:bg-[#10B981]/20 cursor-pointer transition-colors"
              onClick={() => navigate('/admin/rfqs?status=Won')}
              data-testid="won-rfq-card"
            >
              <p className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-[#10B981] mb-2">
                {analytics?.rfq_by_status?.won || 0}
              </p>
              <p className="text-[#94A3B8] font-['JetBrains_Mono'] text-sm sm:text-base">Won</p>
            </div>
            <div 
              className="text-center p-4 rounded-lg bg-[#EF4444]/10 hover:bg-[#EF4444]/20 cursor-pointer transition-colors"
              onClick={() => navigate('/admin/rfqs?status=Lost')}
              data-testid="lost-rfq-card"
            >
              <p className="text-3xl sm:text-4xl font-['JetBrains_Mono'] font-bold text-[#EF4444] mb-2">
                {analytics?.rfq_by_status?.lost || 0}
              </p>
              <p className="text-[#94A3B8] font-['JetBrains_Mono'] text-sm sm:text-base">Lost</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const M2MTile = ({ label, value, hint, icon, testid }) => (
  <div
    className="rounded-lg p-4 border border-[#A855F7]/30 bg-gradient-to-br from-[#1A0B2E] to-[#2D1B47]"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#A855F7] font-['JetBrains_Mono']">
      {icon} <span>{label}</span>
    </div>
    <p className="text-3xl font-['JetBrains_Mono'] font-bold text-white mt-1">
      {value === undefined || value === null ? '—' : value.toLocaleString()}
    </p>
    <p className="text-[10px] text-[#94A3B8] mt-1">{hint}</p>
  </div>
);

const FinanceTile = ({ label, value, hint, Icon, color, testid }) => (
  <div className="bg-[#11141D] border border-[#1E293B] rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-['JetBrains_Mono']" style={{ color }}>
      <Icon size={14} weight="fill" /> {label}
    </div>
    <p className="text-2xl font-['JetBrains_Mono'] font-bold text-white mt-1">{value}</p>
    <p className="text-[10px] text-[#94A3B8] mt-1">{hint}</p>
  </div>
);

export default AdminDashboard;
